/**
 * Dialog used by the Phase detail panel to link the phase to one
 * Backlog milestone (version). A phase can hold multiple links —
 * the caller passes the set of already-linked entries via
 * `existingLinkKeys` so we can grey out duplicates in the picker.
 *
 * Two-step picker:
 *   1. Choose a Backlog project (filtered to the project's configured keys).
 *   2. Choose a milestone within that project (Backlog "version").
 *
 * The "リンクする" button calls `linkPhaseToBacklogMilestone`, which
 * fans out into per-issue WBS task creates. Until the callable resolves
 * we surface a busy spinner — typical milestones complete in <2s but a
 * large milestone (~200 issues) can take ~5-10s end-to-end.
 *
 * Side effect: when the dialog opens with a `backlogProjectNames`
 * snapshot that's missing entries for one or more configured keys, we
 * silently kick `listBacklogProjects` in the background and write the
 * resolved names back to the project doc. That way the picker can
 * always render `名前 (Key)` even when the user hasn't opened the
 * Settings page since the last project rename — and even on the very
 * first link from a fresh deploy where no save has populated the
 * snapshot yet. Failure (no Backlog config / network error) is silent:
 * the dropdown just falls back to the bare key for those entries.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { formatBacklogProjectLabel } from '@mct/shared';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { useProject } from '../../hooks/useFirestoreData';
import { useBacklogIssueMeta } from '../../hooks/useBacklogIssueMeta';
import {
  linkPhaseToBacklogMilestone,
  type LinkPhaseToBacklogMilestoneResult,
} from '../../lib/backlogMilestoneLink';
import { listBacklogProjects } from '../../lib/adminCallables';
import { updateBacklogConfig } from '../../lib/firestore';
import { getDb } from '../../lib/firebase';

export function PhaseBacklogLinkDialog({
  open,
  projectId,
  phaseId,
  phaseName,
  existingLinkKeys,
  onClose,
  onLinked,
}: {
  open: boolean;
  projectId: string;
  phaseId: string;
  phaseName: string;
  /**
   * Set of `${projectKey}:${milestoneId}` already linked on this phase.
   * Used to disable duplicate selections so a user can't re-link a
   * milestone that's already attached. Optional — omitted means "no
   * dupes to check" (effectively first-time link).
   */
  existingLinkKeys?: ReadonlySet<string>;
  onClose: () => void;
  onLinked: (result: LinkPhaseToBacklogMilestoneResult) => void;
}) {
  const [selectedProjectKey, setSelectedProjectKey] = useState<string>('');
  const [selectedMilestoneId, setSelectedMilestoneId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Reset state every time the dialog opens so a previous selection
  // doesn't bleed across phases.
  useEffect(() => {
    if (open) {
      setSelectedProjectKey('');
      setSelectedMilestoneId(null);
      setBusy(false);
      setErrorMsg(null);
    }
  }, [open, phaseId]);

  // The project dropdown reads straight from the configured key list on
  // the project doc — the heavy `listBacklogIssueMeta` probe is only
  // needed for milestones (which we narrow by the chosen key below).
  // Reading the doc-side snapshot also means the dropdown stays
  // populated even when Backlog is briefly unreachable.
  const { data: project } = useProject(open ? projectId : null);
  const backlogProjects = useMemo(() => {
    const keys = project?.backlog?.backlogProjectKeys ?? [];
    const names = project?.backlog?.backlogProjectNames ?? {};
    return keys
      .map((key) => ({ key, name: names[key] ?? '' }))
      .slice()
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [project]);

  // Background refresh of `backlog.backlogProjectNames` when the
  // snapshot is missing entries for any configured key. We deliberately
  // run this in a fire-and-forget effect (failure is silent — UI just
  // shows the bare key) and guard with a ref so a transient
  // `open`-toggle doesn't fire the callable multiple times.
  const autoRefreshAttempted = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      autoRefreshAttempted.current = null;
      return;
    }
    if (!project || !project.backlog) return;
    const cfg = project.backlog;
    const keys = cfg.backlogProjectKeys ?? [];
    if (keys.length === 0) return;
    const names = cfg.backlogProjectNames ?? {};
    const missing = keys.some((k) => !(names[k] && names[k].trim().length > 0));
    if (!missing) return;
    // Once per (open × project doc identity) — don't re-fire on every
    // doc snapshot churn.
    const attemptKey = `${projectId}:${keys.slice().sort().join(',')}`;
    if (autoRefreshAttempted.current === attemptKey) return;
    autoRefreshAttempted.current = attemptKey;

    let cancelled = false;
    (async () => {
      try {
        const res = await listBacklogProjects(projectId);
        if (cancelled || !res.ok) return;
        const nameByKey: Record<string, string> = { ...names };
        for (const k of keys) {
          const live = res.projects.find((p) => p.projectKey === k);
          if (live && live.name && live.name.trim().length > 0) {
            nameByKey[k] = live.name;
          }
        }
        // Only write back when at least one entry changed — avoids a
        // pointless updatedAt bump (and surface churn).
        const changed = keys.some((k) => names[k] !== nameByKey[k]);
        if (!changed) return;
        await updateBacklogConfig(getDb(), projectId, {
          ...cfg,
          backlogProjectNames: nameByKey,
        });
      } catch {
        // silent — fall back to bare keys
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, project, projectId]);

  // Milestone master is still cascade-narrowed by the chosen Backlog
  // project so we don't probe every configured key for nothing once the
  // user has picked one.
  const metaFilter = useMemo(
    () => (selectedProjectKey ? { backlogProjectKeys: [selectedProjectKey] } : undefined),
    [selectedProjectKey],
  );
  const { data: meta, loading: metaLoading } = useBacklogIssueMeta(
    open && selectedProjectKey ? projectId : null,
    metaFilter,
  );

  const milestones = useMemo(() => {
    if (!meta || !meta.ok) return [];
    return meta.milestones
      .filter((m) => !selectedProjectKey || m.backlogProjectKey === selectedProjectKey)
      .slice()
      .sort((a, b) => {
        if (a.archived !== b.archived) return a.archived ? 1 : -1;
        return a.name.localeCompare(b.name);
      });
  }, [meta, selectedProjectKey]);

  const isDuplicate = useMemo(() => {
    if (!selectedProjectKey || selectedMilestoneId == null) return false;
    return (
      existingLinkKeys?.has(`${selectedProjectKey}:${selectedMilestoneId}`) ===
      true
    );
  }, [existingLinkKeys, selectedProjectKey, selectedMilestoneId]);

  const canSubmit =
    !busy &&
    selectedProjectKey.length > 0 &&
    selectedMilestoneId !== null &&
    !isDuplicate;

  const handleSubmit = async () => {
    if (!canSubmit || selectedMilestoneId === null) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      const result = await linkPhaseToBacklogMilestone({
        projectId,
        phaseId,
        backlogProjectKey: selectedProjectKey,
        milestoneId: selectedMilestoneId,
      });
      if (!result.ok) {
        setErrorMsg(result.message);
        setBusy(false);
        return;
      }
      onLinked(result);
      onClose();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? () => undefined : onClose}
      title={`「${phaseName}」と Backlog マイルストンを連携`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            キャンセル
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {busy ? (
              <span className="inline-flex items-center gap-1.5">
                <Spinner className="h-3.5 w-3.5" /> 取り込み中…
              </span>
            ) : (
              '連携する'
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-xs text-slate-500">
          選択したマイルストン配下の課題を、フェーズ直下の WBS タスクとして自動で取り込みます。
        </p>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            Backlog プロジェクト
          </label>
          <select
            className="input"
            value={selectedProjectKey}
            disabled={busy}
            onChange={(e) => {
              setSelectedProjectKey(e.target.value);
              setSelectedMilestoneId(null);
            }}
            data-testid="phase-link-project"
          >
            <option value="">— プロジェクトを選択 —</option>
            {backlogProjects.map((p) => (
              <option key={p.key} value={p.key}>
                {formatBacklogProjectLabel(p.key, p.name)}
              </option>
            ))}
          </select>
          {project && backlogProjects.length === 0 ? (
            <p className="mt-1 text-[11px] text-amber-700">
              連携プロジェクトが未設定です。プロジェクト設定で
              Backlog プロジェクトを 1 件以上選択してください。
            </p>
          ) : null}
          {meta && !meta.ok ? (
            <p className="mt-1 text-[11px] text-red-600">{meta.message}</p>
          ) : null}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            マイルストン (version)
          </label>
          <select
            className="input"
            value={selectedMilestoneId == null ? '' : String(selectedMilestoneId)}
            disabled={busy || !selectedProjectKey || metaLoading}
            onChange={(e) => {
              const v = e.target.value;
              setSelectedMilestoneId(v === '' ? null : Number(v));
            }}
            data-testid="phase-link-milestone"
          >
            <option value="">— マイルストンを選択 —</option>
            {milestones.map((m) => {
              const alreadyLinked =
                existingLinkKeys?.has(`${selectedProjectKey}:${m.id}`) === true;
              return (
                <option
                  key={m.id}
                  value={String(m.id)}
                  disabled={alreadyLinked}
                >
                  {m.name}
                  {m.archived ? '（アーカイブ済）' : ''}
                  {alreadyLinked ? '（連携済み）' : ''}
                </option>
              );
            })}
          </select>
          {selectedProjectKey && !metaLoading && milestones.length === 0 ? (
            <p className="mt-1 text-[11px] text-amber-700">
              このプロジェクトには「使用中」のマイルストンが見つかりませんでした。Backlog
              側で課題が紐付いているか確認してください。
            </p>
          ) : null}
          {isDuplicate ? (
            <p className="mt-1 text-[11px] text-amber-700">
              そのマイルストンは既にこのフェーズに連携されています。
            </p>
          ) : null}
        </div>
        {errorMsg ? (
          <p className="text-[11px] text-red-600" data-testid="phase-link-error">
            {errorMsg}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
